import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { 
  UserGroupIcon, 
  CurrencyDollarIcon, 
  ClockIcon,
  CheckCircleIcon,
  CalendarIcon,
  AcademicCapIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline'
import api from '../utils/api'

export default function InstructorDashboard() {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState(null)
  const [students, setStudents] = useState([])
  const [earnings, setEarnings] = useState([])

  useEffect(() => {
    fetchDashboardData()
  }, [])

  const fetchDashboardData = async () => {
    try {
      setLoading(true)
      
      // Fetch dashboard stats and students
      const [dashboardRes, studentsRes, earningsRes] = await Promise.all([
        api.get('/instructor/dashboard'),
        api.get('/instructor/students'),
        api.get('/instructor/earnings')
      ])
      
      setStats(dashboardRes.data.stats)
      setStudents(studentsRes.data)
      setEarnings(earningsRes.data)
    } catch (error) {
      console.error('Error fetching instructor dashboard:', error)
      console.error('Error response:', error.response)
      
      if (error.response?.status === 403) {
        alert('You do not have instructor access. Please contact support.')
      }
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Instructor Dashboard</h1>
        <p className="text-gray-600 mt-2">Manage your elite students and track your earnings</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Students</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats?.active_students || 0}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-lg">
              <UserGroupIcon className="h-8 w-8 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending Earnings</p>
              <p className="text-3xl font-bold text-green-600 mt-2">${stats?.pending_earnings || 0}</p>
            </div>
            <div className="bg-green-100 p-3 rounded-lg">
              <CurrencyDollarIcon className="h-8 w-8 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Earned</p>
              <p className="text-3xl font-bold text-purple-600 mt-2">${stats?.total_earnings || 0}</p>
            </div>
            <div className="bg-purple-100 p-3 rounded-lg">
              <CurrencyDollarIcon className="h-8 w-8 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Sessions Complete</p>
              <p className="text-3xl font-bold text-indigo-600 mt-2">{stats?.completed_sessions || 0}</p>
            </div>
            <div className="bg-indigo-100 p-3 rounded-lg">
              <CheckCircleIcon className="h-8 w-8 text-indigo-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Students List */}
      <div className="bg-white rounded-lg border border-gray-200 mb-8">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Your Students</h2>
        </div>
        
        {students.length === 0 ? (
          <div className="p-12 text-center">
            <UserGroupIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No students assigned yet</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {students.map((assignment) => (
              <div key={assignment.id} className="p-6 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {assignment.student.name}
                      </h3>
                      <span className={`px-2 py-1 text-xs font-medium rounded ${
                        assignment.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {assignment.status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">{assignment.student.email}</p>
                    
                    {/* Progress Bars */}
                    <div className="space-y-3">
                      {/* Course Progress */}
                      <div>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-gray-600">Course Progress</span>
                          <span className="font-medium text-gray-900">
                            {assignment.course_progress.completed_modules}/{assignment.course_progress.total_modules} modules ({assignment.course_progress.percentage}%)
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${assignment.course_progress.percentage}%` }}
                          />
                        </div>
                      </div>

                      {/* Session Progress */}
                      <div>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-gray-600">Elite Sessions</span>
                          <span className="font-medium text-gray-900">
                            {assignment.session_progress.completed}/{assignment.session_progress.total} completed ({assignment.session_progress.percentage}%)
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-green-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${assignment.session_progress.percentage}%` }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Session List */}
                    <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-2">
                      {assignment.sessions.map((session) => (
                        <div 
                          key={session.id}
                          className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${
                            session.is_completed 
                              ? 'bg-green-100 text-green-800' 
                              : session.scheduled_at 
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {session.is_completed && <CheckCircleIcon className="h-3 w-3" />}
                          {session.scheduled_at && !session.is_completed && <CalendarIcon className="h-3 w-3" />}
                          <span className="truncate">{session.type_name}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="ml-6">
                    <button 
                      onClick={() => navigate(`/instructor/student/${assignment.id}`)}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                    >
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent Earnings */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Recent Earnings</h2>
        </div>
        
        {earnings.length === 0 ? (
          <div className="p-12 text-center">
            <CurrencyDollarIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No earnings yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {earnings.map((earning) => (
                  <tr key={earning.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{earning.student.name}</div>
                        <div className="text-sm text-gray-500">{earning.student.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-semibold text-gray-900">${earning.amount}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded ${
                        earning.status === 'paid' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {earning.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {earning.paid_at 
                        ? new Date(earning.paid_at).toLocaleDateString() 
                        : new Date(earning.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
